import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Button, Form, Table, Modal, Badge, Alert, Dropdown, ButtonGroup, InputGroup, OverlayTrigger, Tooltip, ListGroup, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import Pagination from '../../../../Pagination';
import { PageLoader } from '../../../../../Components/NoDataFoundTable/NoDataFoundTable';
import DatasetViewerToolbar from './Toolbar';
import DatasetViewerFilterPanel from './FilterPanel';
import DatasetViewerContextMenu from './ContextMenu';
import DatasetViewerModals from './Modals';
import DatasetViewerTableView from './TableView';
import {
  cellText, inferColumnTypes, compareCells, parseFilterExpression,
  isColumnFilterActive, validateColumnFilter, matchColumnFilter, columnFilterLabel, defaultColumnFilterOp
} from './filterUtils';

const LABELS = {
  STUDYID: "Study Identifier", USUBJID: "Unique Subject Identifier",
  SUBJID: "Subject Identifier", SITEID: "Study Site Identifier",
  DOMAIN: "Domain Abbreviation", AESEQ: "Sequence Number", AESPID: "Sponsor-Defined Identifier",
  SAFFL: "Safety Population Flag", SAFFN: "Safety Population Flag (N)",
  AGE: "Age", SEX: "Sex", RACE: "Race", ARM: "Description of Planned Arm",
  TRT01A: "Actual Treatment for Period 01",
  AETERM: "Reported Term for the Adverse Event", AEDECOD: "Dictionary-Derived Term",
  AESEV: "Severity/Intensity", AESER: "Serious Event", AEREL: "Causality",
  AEOUT: "Outcome of Adverse Event",
  VISIT: "Visit Name", VISITNUM: "Visit Number", PARAM: "Parameter",
  PARAMCD: "Parameter Code", AVAL: "Analysis Value", AVALU: "Analysis Value Unit",
  ADT: "Analysis Date", ANL01FL: "Analysis Flag 01"
};

const buildDataset = () => {
  const study = "NCS-1080-25-CS";
  const site = "NCS";
  const subs = ["02", "01", "04", "02", "09R1", "01", "11", "11", "11", "13"];
  const arms = ["Placebo", "NCS-1080 Low", "NCS-1080 High"];
  const sexes = ["F", "M"];
  const races = ["WHITE", "BLACK OR AFRICAN AMERICAN", "ASIAN"];
  const aeTerms = [
    ["Headache", "Headache", "MILD"],
    ["Nausea", "Nausea", "MODERATE"],
    ["Fatigue", "Fatigue", "MILD"],
    ["Dizziness", "Dizziness", "MILD"],
    ["Rash", "Rash maculo-papular", "MODERATE"],
    ["Pyrexia", "Pyrexia", "MILD"],
    ["Vomiting", "Vomiting", "SEVERE"],
    ["Diarrhoea", "Diarrhoea", "MODERATE"],
    ["Cough", "Cough", "MILD"],
    ["Insomnia", "Insomnia", "MILD"]
  ];
  const rels = ["RELATED", "NOT RELATED", "POSSIBLY RELATED"];
  const outs = ["RECOVERED/RESOLVED", "RECOVERING/RESOLVING", "NOT RECOVERED/NOT RESOLVED"];
  const params = [
    ["Diastolic Blood Pressure (mmHg)", "DIABP", "mmHg", 60, 95],
    ["Systolic Blood Pressure (mmHg)", "SYSBP", "mmHg", 100, 150],
    ["Pulse Rate (beats/min)", "PULSE", "beats/min", 55, 100],
    ["Body Temperature (C)", "TEMP", "C", 36.1, 38.2]
  ];
  const visits = [["Baseline", 1], ["Week 2", 2], ["Week 4", 4], ["Week 8", 8]];
  const rows = [];
  let n = 0;
  for (let i = 0; i < 41; i++) {
    const subj = subs[i % subs.length];
    const usubj = `${study}-${site}-${subj}`;
    const arm = arms[i % arms.length];
    const ae = aeTerms[i % aeTerms.length];
    const pr = params[i % params.length];
    const vs = visits[i % visits.length];
    const seq = (i % 3) + 1;
    const span = pr[4] - pr[3];
    let val = pr[3] + span * ((Math.sin(n * 1.3) + 1) / 2);
    val = pr[3] < 40 ? +val.toFixed(1) : Math.round(val);
    const anl = (n % 6 === 0) ? "N" : "Y";
    rows.push({
      fieldRow: {
        STUDYID: study, USUBJID: usubj, SUBJID: subj, SITEID: site,
        DOMAIN: "AE", AESEQ: seq, AESPID: seq,
        SAFFL: "Y", SAFFN: 1, AGE: 34 + (i % 45), SEX: sexes[i % 2],
        RACE: races[i % races.length], ARM: arm, TRT01A: arm,
        AETERM: ae[0], AEDECOD: ae[1], AESEV: ae[2],
        AESER: (ae[2] === "SEVERE" ? "Y" : "N"),
        AEREL: rels[i % rels.length], AEOUT: outs[i % outs.length],
        VISIT: vs[0], VISITNUM: vs[1], PARAM: pr[0], PARAMCD: pr[1],
        AVAL: val, AVALU: pr[2],
        ADT: "2026-0" + (1 + (i % 6)) + "-" + String(5 + (i % 20)).padStart(2, "0"),
        ANL01FL: anl
      }
    });
    n++;
  }
  return rows;
};

const DATASET = buildDataset();

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;")
  .replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const highlight = (text, q) => {
  if (!q) return esc(text);
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return esc(text);
  return esc(text.slice(0, i)) + '<span class="bg-warning text-dark rounded px-1">' + esc(text.slice(i, i + q.length)) + '</span>' + esc(text.slice(i + q.length));
};

const DatasetViewer = ({
  data = DATASET,
  columns,
  hiddenColumns,
  startIndex = 0,
  onRowClick,
  onCellClick,
  showSrColumn = true,
  srColumnLabel = "Sr.",
  isFullScreen = false,
  setIsFullScreen = () => { },
  sorts = [],
  handleSort = () => { },
  clearSorts = () => { },
  quick = "",
  setQuick = () => { },
  showLabels = false,
  setShowLabels = () => { },
  toggle = () => { },
  setToggle = () => { },
  exportCSVFile = () => { },
  exportExcelFile = () => { },
  exportJSONFile = () => { },
  setShowFilterPanel,
  showFilterPanel,
  setShowColsPanel,
  showColsPanel,
  resetKey = 0,
  loading = false,
  csvLoader = false,
  excelLoader = false,
  JSONLoader = false,
  studyPermission = {},
  totalPage,
  pageNumber,
  itemsPerPage,
  totalItems,
  setPageNumber,
  setItemsPerPage
}) => {

  const [filter, setFilter] = useState("");
  const [filterErr, setFilterErr] = useState("");
  const [hidden, setHidden] = useState(() => {
    const initialHidden = new Set();
    Object.keys(hiddenColumns).forEach(key => {
      if (hiddenColumns[key]) initialHidden.add(key);
    });
    return initialHidden;
  });
  const [colFilters, setColFilters] = useState({});
  const [widths, setWidths] = useState({});
  const [order, setOrder] = useState(columns.map((_, i) => i));

  const [colSearch, setColSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [showColFilterModal, setShowColFilterModal] = useState(false);
  const [colFilterTarget, setColFilterTarget] = useState(null);
  const [colFilterOp, setColFilterOp] = useState("contains");
  const [colFilterVal, setColFilterVal] = useState("");
  const [colFilterVal2, setColFilterVal2] = useState("");
  const [contextMenu, setContextMenu] = useState(null);
  const [toast, setToast] = useState(null);
  const [showSetWidthModal, setShowSetWidthModal] = useState(false);
  const [widthTarget, setWidthTarget] = useState(null);
  const [frozen, setFrozen] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [resizingCol, setResizingCol] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartW, setResizeStartW] = useState(0);

  const tableRef = useRef(null);

  const [processing, setProcessing] = useState(false);
  const processingTimer = useRef(null);
  const triggerProcessing = (ms = 200) => {
    if (processingTimer.current) clearTimeout(processingTimer.current);
    setProcessing(true);
    processingTimer.current = setTimeout(() => {
      setProcessing(false);
      processingTimer.current = null;
    }, ms);
  };

  const colTypes = useMemo(() => inferColumnTypes(data, columns), [data, columns]);
  const colType = (c) => colTypes[c] || "character";

  // Display order of the column indices; indices that do not exist (any more) are dropped, new ones appended
  const colOrder = useMemo(() => {
    const kept = order.filter(i => i < columns.length);
    return kept.concat(columns.map((_, i) => i).filter(i => !kept.includes(i)));
  }, [order, columns]);

  const visibleColumns = useMemo(() =>
    colOrder.filter(i => !hidden.has(columns[i])).map(i => columns[i]),
    [colOrder, columns, hidden]
  );

  const computed = useMemo(() => {
    let rows = [...data];
    let filterActive = false;
    let filterErrMsg = "";

    const q = quick.trim().toLowerCase();
    if (q) {
      rows = rows.filter(row =>
        visibleColumns.some(col => cellText(row.fieldRow[col]).toLowerCase().includes(q))
      );
    }

    if (filter.trim()) {
      try {
        const predicate = parseFilterExpression(filter.trim(), columns);
        rows = rows.filter(row => predicate(row, visibleColumns));
        filterActive = true;
      } catch (e) {
        filterErrMsg = e.message || 'Invalid filter expression';
      }
    }

    const colF = Object.entries(colFilters).filter(([k, f]) => +k < columns.length && isColumnFilterActive(f));
    if (colF.length) {
      filterActive = true;
      rows = rows.filter(row =>
        colF.every(([k, f]) => {
          const col = columns[+k];
          return matchColumnFilter(row.fieldRow[col], f, colTypes[col] || "character");
        })
      );
    }

    const activeSorts = sorts.filter(s => s.col >= 0 && s.col < columns.length);
    if (activeSorts.length) {
      rows.sort((a, b) => {
        for (const s of activeSorts) {
          const col = columns[s.col];
          const c = compareCells(a.fieldRow[col], b.fieldRow[col], colTypes[col]);
          if (c) return c * (s.dir === "desc" ? -1 : 1);
        }
        return 0;
      });
    }

    return { rows, filterActive, colF, filterErr: filterErrMsg };
  }, [data, visibleColumns, quick, filter, colFilters, sorts, columns, colTypes]);

  const { rows, filterActive, colF, filterErr: computedFilterErr } = computed;

  const total = data.length;
  const shown = rows.length;
  // Paging is done by the server (parent Pagination); the viewer shows every row of the loaded page
  const pageRows = rows;
  const searchActive = !!quick.trim() || filterActive;

  useEffect(() => {
    triggerProcessing(180);
    return () => {
      if (processingTimer.current) {
        clearTimeout(processingTimer.current);
        processingTimer.current = null;
      }
    };
  }, [quick, filter, JSON.stringify(colFilters), JSON.stringify(sorts), JSON.stringify(order), Array.from(hidden).join(',')]);

  const getWidth = (col) => {
    const idx = columns.indexOf(col);
    return widths[idx] || 150;
  };

  const setColumnWidth = (col, w) => {
    const idx = columns.indexOf(col);
    setWidths(prev => ({ ...prev, [idx]: w }));
  };

  const autoFit = (col) => {
    const idx = columns.indexOf(col);
    let max = col.length;
    const lim = Math.min(data.length, 500);
    for (let i = 0; i < lim; i++) {
      const s = String(data[i].fieldRow[col] == null ? "" : data[i].fieldRow[col]);
      if (s.length > max) max = s.length;
    }
    setWidths(prev => ({ ...prev, [idx]: Math.min(420, Math.max(70, max * 7.4 + 34)) }));
  };

  const autoFitAll = () => {
    const newWidths = { ...widths };
    visibleColumns.forEach(col => {
      const idx = columns.indexOf(col);
      let max = col.length;
      const lim = Math.min(data.length, 500);
      for (let i = 0; i < lim; i++) {
        const s = String(data[i].fieldRow[col] == null ? "" : data[i].fieldRow[col]);
        if (s.length > max) max = s.length;
      }
      newWidths[idx] = Math.min(420, Math.max(70, max * 7.4 + 34));
    });
    setWidths(newWidths);
  };

  const resetWidth = (col) => {
    const idx = columns.indexOf(col);
    const newWidths = { ...widths };
    delete newWidths[idx];
    setWidths(newWidths);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1500);
  };

  const copyText = (text, msg) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => showToast(msg || "Copied"));
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (e) { }
      ta.remove();
      showToast(msg || "Copied");
    }
  };

  const firstSort = sorts[0];

  const toggleColVis = (col) => {
    const newHidden = new Set(hidden);
    if (newHidden.has(col)) {
      newHidden.delete(col);
    } else {
      if (visibleColumns.length <= 1) {
        showToast("At least one column must remain visible.");
        return;
      }
      newHidden.add(col);
    }
    setHidden(newHidden);
  };

  const showAllCols = () => setHidden(new Set());

  const hideAllCols = () => {
    if (columns.length <= 1) {
      showToast("At least one column must remain visible.");
      return;
    }
    setHidden(new Set(columns.slice(1)));
  };

  const clearColFilter = (ci) => {
    const newColFilters = { ...colFilters };
    delete newColFilters[ci];
    setColFilters(newColFilters);
  };

  const clearAllFilters = () => {
    setFilter("");
    setFilterErr("");
    setColFilters({});
    setQuick("");
  };

  const handleColFilterApply = () => {
    const ci = colFilterTarget;
    if (ci === null) return;
    const op = colFilterOp;
    const f = (op === "blank" || op === "nonblank") ? { op } : { op, val: colFilterVal, val2: colFilterVal2 };
    const newColFilters = { ...colFilters };
    if (isColumnFilterActive(f)) {
      const err = validateColumnFilter(f);
      if (err) {
        showToast(err);
        return;
      }
      newColFilters[ci] = f;
    } else {
      // Applying an empty value removes the filter of this column
      delete newColFilters[ci];
    }
    setColFilters(newColFilters);
    setShowFilterPanel(true);
    setShowColFilterModal(false);
  };

  const openColFilter = (col) => {
    const ci = columns.indexOf(col);
    const defOp = defaultColumnFilterOp(colType(col));
    const cur = colFilters[ci] || { op: defOp, val: "", val2: "" };
    setColFilterTarget(ci);
    setColFilterOp(cur.op || defOp);
    setColFilterVal(cur.val || "");
    setColFilterVal2(cur.val2 || "");
    setShowColFilterModal(true);
  };

  const filterByValue = (row, col) => {
    closeContextMenu();
    const val = cellText(row.fieldRow[col]).trim();
    const ci = columns.indexOf(col);
    setColFilters(prev => ({ ...prev, [ci]: val === "" ? { op: "blank" } : { op: "eq", val } }));
    setShowFilterPanel(true);
  };

  // Moves a column among the visible columns (hidden columns keep their place)
  const moveCol = (ci, where) => {
    const vis = colOrder.filter(i => !hidden.has(columns[i]));
    const vpos = vis.indexOf(ci);
    if (vpos < 0) return;
    const newOrder = colOrder.filter(i => i !== ci);
    let target;
    if (where === "left") {
      if (vpos === 0) return;
      target = newOrder.indexOf(vis[vpos - 1]);
    } else if (where === "right") {
      if (vpos === vis.length - 1) return;
      target = newOrder.indexOf(vis[vpos + 1]) + 1;
    } else if (where === "first") {
      target = 0;
    } else {
      target = newOrder.length;
    }
    newOrder.splice(target, 0, ci);
    setOrder(newOrder);
  };

  const freezeTo = (col) => {
    const pos = visibleColumns.indexOf(col);
    if (pos >= 0) setFrozen(pos + 1);
  };

  const unfreezeAll = () => setFrozen(0);

  const openContextMenu = (e, items) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, items });
  };

  const closeContextMenu = () => setContextMenu(null);

  const showGenericModal = (title, body, onPrimary, primaryLabel = "OK") => {
    setModalContent({ title, body, onPrimary, primaryLabel });
    setShowModal(true);
  };

  const showRowDetails = (row, rowIndex) => {
    closeContextMenu();
    const body = (
      <Table className="m-0 custom-table" hover striped>
        <tbody>
          {visibleColumns.map(col => (
            <tr key={col}>
              <td style={{ whiteSpace: "nowrap", verticalAlign: "top", fontWeight: 600 }}>{col}</td>
              <td>{esc(row.fieldRow[col])}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
    showGenericModal(
      `Row Details — record ${rowIndex + 1}`,
      body,
      () => copyText(visibleColumns.map(col => `${col}\t${row.fieldRow[col]}`).join("\n"), "Row details copied"),
      "Copy"
    );
  };

  const showViewFullValue = (row, col) => {
    closeContextMenu();
    const val = String(row.fieldRow[col] == null ? "" : row.fieldRow[col]);
    const body = (
      <>
        <div className="text-muted small">{LABELS[col] || col} · type {colType(col)}</div>
        <Form.Control
          as="textarea"
          value={val}
          readOnly
          style={{ height: "110px", fontFamily: "monospace", resize: "vertical" }}
        />
      </>
    );
    showGenericModal(
      `${col} — Full Value`,
      body,
      () => copyText(val, "Value copied"),
      "Copy"
    );
  };

  const showColProps = (col) => {
    closeContextMenu();
    const ty = colType(col);
    let present = 0, missing = 0;
    const uniq = new Set();
    let min = null, max = null;
    for (const row of data) {
      const raw = row.fieldRow[col];
      if (raw == null || String(raw).trim() === "") { missing++; continue; }
      present++;
      uniq.add(String(raw));
      if (min == null || compareCells(raw, min, ty) < 0) min = raw;
      if (max == null || compareCells(raw, max, ty) > 0) max = raw;
    }
    const minD = min == null ? null : String(min), maxD = max == null ? null : String(max);

    const rowT = (k, v) => (
      <tr key={k}>
        <th style={{ textAlign: "left", color: "var(--bs-secondary)", fontWeight: 600, padding: "4px 14px 4px 0", whiteSpace: "nowrap" }}>{k}</th>
        <td style={{ padding: "4px 0" }}>{esc(v)}</td>
      </tr>
    );

    const body = (
      <Table size="sm" style={{ margin: 0 }}>
        <tbody>
          {rowT("Variable", col)}
          {rowT("Label", LABELS[col] || col)}
          {rowT("Type", ty)}
          {rowT("Position", columns.indexOf(col) + 1 + " of " + columns.length)}
          {rowT("Non-missing", present)}
          {rowT("Missing", missing)}
          {rowT("Unique values", uniq.size)}
          {rowT("Minimum", minD == null ? "—" : minD)}
          {rowT("Maximum", maxD == null ? "—" : maxD)}
        </tbody>
      </Table>
    );
    showGenericModal(`Column Properties — ${col}`, body, null, "Close");
  };

  const openSetWidth = (col) => {
    closeContextMenu();
    setWidthTarget(col);
    setShowSetWidthModal(true);
  };

  const copyCell = (row, col) => {
    closeContextMenu();
    copyText(String(row.fieldRow[col] == null ? "" : row.fieldRow[col]), "Cell copied");
  };

  const copyRow = (row) => {
    closeContextMenu();
    copyText(visibleColumns.map(col => row.fieldRow[col]).join("\t"), "Row copied");
  };

  const copyColumn = (col) => {
    closeContextMenu();
    const values = rows.map(r => r.fieldRow[col]);
    copyText(values.join("\n"), `Column ${col} copied`);
  };

  const download = (content, name, mime) => {
    const blob = new Blob([content], { type: mime || "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
  };

  const exportData = (fmt) => {
    const cols = visibleColumns;
    if (fmt === "json") {
      const exportRows = rows.map(r => {
        const obj = {};
        cols.forEach(col => obj[col] = r.fieldRow[col]);
        return obj;
      });
      download(JSON.stringify(exportRows, null, 2), "dataset.json", "application/json");
    } else if (fmt === "xlsx") {
      const th = cols.map(c => `<th>${esc(c)}</th>`).join("");
      const tr = rows.map(r => `<tr>${cols.map(c => `<td>${esc(r.fieldRow[c])}</td>`).join("")}</tr>`).join("");
      const htmlDoc = `<html><head><meta charset="utf-8"></head><body><table border="1">` +
        `<thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table></body></html>`;
      download(htmlDoc, "dataset.xls", "application/vnd.ms-excel");
    } else {
      const q = (s) => {
        s = String(s == null ? "" : s);
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      };
      const lines = [cols.map(q).join(",")].concat(rows.map(r => cols.map(c => q(r.fieldRow[c])).join(",")));
      download(lines.join("\n"), "dataset.csv", "text/csv;charset=utf-8");
    }
  };

  const reset = () => {
    setQuick("");
    setFilter("");
    setFilterErr("");
    setHidden(new Set(Object.keys(hiddenColumns).filter(k => hiddenColumns[k])));
    setColFilters({});
    setWidths({});
    setOrder(columns.map((_, i) => i));
    setShowLabels(false);
    setShowFilterPanel(false);
    setShowColsPanel(false);
    setColSearch("");
    setFrozen(0);
    setSelectedRow(null);
  };

  // Only a Reset click (resetKey change) resets; on mount the parent's search / panels are kept
  const resetKeyRef = useRef(resetKey);
  useEffect(() => {
    if (resetKeyRef.current === resetKey) return;
    resetKeyRef.current = resetKey;
    reset();
  }, [resetKey]);

  // Table properties can arrive after the data: apply the saved hidden columns when they change
  const hiddenColumnsKey = Object.keys(hiddenColumns).filter(k => hiddenColumns[k]).sort().join("\u0001");
  const hiddenColumnsKeyRef = useRef(hiddenColumnsKey);
  useEffect(() => {
    if (hiddenColumnsKeyRef.current === hiddenColumnsKey) return;
    hiddenColumnsKeyRef.current = hiddenColumnsKey;
    setHidden(new Set(hiddenColumnsKey ? hiddenColumnsKey.split("\u0001") : []));
  }, [hiddenColumnsKey]);

  const getHeaderMenuItems = (col, ci) => [
    { label: "Sort Ascending", icon: "▲", action: () => { handleSort(ci, false, "asc"); closeContextMenu(); } },
    { label: "Sort Descending", icon: "▼", action: () => { handleSort(ci, false, "desc"); closeContextMenu(); } },
    { label: "Clear Sort", icon: "✕", action: () => { clearSorts(); closeContextMenu(); } },
    { label: "Filter Column…", icon: "▼", action: () => { openColFilter(col); closeContextMenu(); } },
    {
      label: "Hide Column", icon: "⊘", action: () => {
        const newHidden = new Set(hidden);
        if (visibleColumns.length <= 1) {
          showToast("At least one column must remain visible.");
          closeContextMenu();
          return;
        }
        newHidden.add(col);
        setHidden(newHidden);
        closeContextMenu();
      }
    },
    { label: "Auto Fit Column", icon: "↔", action: () => { autoFit(col); closeContextMenu(); } },
    { label: "Auto Fit All Columns", icon: "↔", action: () => { autoFitAll(); closeContextMenu(); } },
    { label: "Set Column Width…", icon: "▭", action: () => { openSetWidth(col); closeContextMenu(); } },
    { label: "Reset Column Width", icon: "⟲", action: () => { resetWidth(col); closeContextMenu(); } },
    { label: "Freeze Up To Here", icon: "❄", action: () => { freezeTo(col); closeContextMenu(); } },
    { label: "Unfreeze All", icon: "✷", action: () => { unfreezeAll(); closeContextMenu(); } },
    { label: "Move Left", icon: "⇤", action: () => { moveCol(ci, "left"); closeContextMenu(); } },
    { label: "Move Right", icon: "⇥", action: () => { moveCol(ci, "right"); closeContextMenu(); } },
    { label: "Move to First", icon: "⏮", action: () => { moveCol(ci, "first"); closeContextMenu(); } },
    { label: "Move to Last", icon: "⏭", action: () => { moveCol(ci, "last"); closeContextMenu(); } },
    { label: "Copy Column", icon: "⧉", action: () => { copyColumn(col); closeContextMenu(); } },
    { label: "Column Properties…", icon: "ⓘ", action: () => { showColProps(col); closeContextMenu(); } },
  ];

  const getCellMenuItems = (row, col) => [
    { label: "Copy Cell", icon: "⧉", action: () => copyCell(row, col) },
    { label: "Copy Row", icon: "☰", action: () => copyRow(row) },
    { label: "Copy Column", icon: "▥", action: () => copyColumn(col) },
    { label: "View Full Value…", icon: "🔍", action: () => showViewFullValue(row, col) },
    { label: "Filter by This Value", icon: "▼", action: () => filterByValue(row, col) },
    { label: "Clear Filter", icon: "✕", action: () => { const ci = columns.indexOf(col); clearColFilter(ci); closeContextMenu(); } },
    { label: "View Row Details…", icon: "ⓘ", action: () => showRowDetails(row, data.indexOf(row)) },
  ];

  const onResizeMouseDown = (e, col) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = getWidth(col);
    const onMouseMove = (ev) => {
      const w = Math.max(60, startW + (ev.clientX - startX));
      setColumnWidth(col, w);
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeContextMenu();
        if (isFullScreen) setIsFullScreen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isFullScreen]);

  useEffect(() => {
    const handleClick = () => closeContextMenu();
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    if (!showFilterPanel) {
      setFilter("");
      setFilterErr("");
      setColFilters({});
    }
  }, [showFilterPanel]);

  return (
    <div
      className={`d-flex flex-column ${isFullScreen ? "full-screen-component" : ""}`}
    >
      <DatasetViewerToolbar
        isFullScreen={isFullScreen}
        toggle={toggle}
        exportCSVFile={exportCSVFile}
        exportExcelFile={exportExcelFile}
        exportJSONFile={exportJSONFile}
        csvLoader={csvLoader}
        excelLoader={excelLoader}
        JSONLoader={JSONLoader}
        studyPermission={studyPermission}
        setIsFullScreen={setIsFullScreen}
      />
      <DatasetViewerFilterPanel
        showFilterPanel={showFilterPanel}
        filter={filter}
        setFilter={setFilter}
        setFilterErr={setFilterErr}
        computedFilterErr={computedFilterErr}
        colFilters={colFilters}
        columns={columns}
        clearColFilter={clearColFilter}
        columnFilterLabel={columnFilterLabel}
        filterActive={filterActive}
        shown={shown}
      />
      <div className={isFullScreen ? "overflow-auto" : ""}>
        <DatasetViewerTableView
          processing={processing || loading}
          tableRef={tableRef}
          isFullScreen={isFullScreen}
          showSrColumn={showSrColumn}
          srColumnLabel={srColumnLabel}
          visibleColumns={visibleColumns}
          columns={columns}
          sorts={sorts}
          handleSort={handleSort}
          openContextMenu={openContextMenu}
          getHeaderMenuItems={getHeaderMenuItems}
          getWidth={getWidth}
          showLabels={showLabels}
          colType={colType}
          LABELS={LABELS}
          pageRows={pageRows}
          startIndex={startIndex}
          data={data}
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          onRowClick={onRowClick}
          onCellClick={onCellClick}
          getCellMenuItems={getCellMenuItems}
          onResizeMouseDown={onResizeMouseDown}
          autoFit={autoFit}
          frozen={frozen}
          clearColFilter={clearColFilter}
          visibleColumnsLength={visibleColumns.length + (showSrColumn ? 1 : 0)}
          esc={esc}
          highlight={highlight}
          showRowDetails={showRowDetails}
        />
      </div>
      {!processing &&
        <div className="position-sticky bottom-0 start-0 bg-white border-top">
          {(searchActive || sorts.length > 0) && !loading &&
            <div className="small text-muted px-2 pt-1">
              Showing {shown} of {total} rows of this page. Search, filters and sorting apply to the loaded page only.
            </div>
          }
          <Pagination
            totalPage={totalPage}
            pageNumber={pageNumber}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            setPageNumber={setPageNumber}
            setItemsPerPage={setItemsPerPage}
          />
        </div>
      }
      <DatasetViewerContextMenu
        contextMenu={contextMenu}
        closeContextMenu={closeContextMenu}
      />

      <DatasetViewerModals
        showModal={showModal}
        modalContent={modalContent}
        setShowModal={setShowModal}
        showColFilterModal={showColFilterModal}
        setShowColFilterModal={setShowColFilterModal}
        colFilterTarget={colFilterTarget}
        columns={columns}
        colType={colType}
        colFilterOp={colFilterOp}
        setColFilterOp={setColFilterOp}
        colFilterVal={colFilterVal}
        setColFilterVal={setColFilterVal}
        colFilterVal2={colFilterVal2}
        setColFilterVal2={setColFilterVal2}
        handleColFilterApply={handleColFilterApply}
        showSetWidthModal={showSetWidthModal}
        setShowSetWidthModal={setShowSetWidthModal}
        widthTarget={widthTarget}
        getWidth={getWidth}
        setColumnWidth={setColumnWidth}
      />

      {toast && (
        <div
          className="position-fixed start-50 translate-middle-x bg-dark text-white rounded px-3 py-2"
          style={{ bottom: "22px", zIndex: 2000 }}
        >
          {toast}
        </div>
      )}
    </div>
  );
};

export default DatasetViewer;